import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import './Wishlist.scss'
import { PageLoadLoader } from "../Components/Loader";
import { useProfile } from "../Contexts/ProfileContext";
import rupeesIcon from "../Cart/Assets/rupeesIcon.svg";
import returnIcon from "../Cart/Assets/returnIcon.svg";
import deliveryIcon from "../Cart/Assets/delivery.svg";
import { BsCartPlus, BsTrash, BsStarFill, BsEye, BsCheckCircle } from "react-icons/bs";
import axios from "axios";
import { isFullyOutOfStockFromSizeMap, totalStockFromSizeMap } from "../utils/inventoryFromSizeMap";
import { useToast } from "../Contexts/ToastContext";
import { saveCheckoutDraftForBuyNow } from "../utils/checkoutDraft";
import { ensureMediaUrl } from "../utils/mediaUrl";

const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

/** Same logic as mob wishlist size sheet — uses `item.size` map when present. */
function getWishlistSizeOptions(item) {
    const sizeMap =
        item.size && typeof item.size === "object" && !Array.isArray(item.size)
            ? item.size
            : null;
    let keys;
    if (sizeMap && Object.keys(sizeMap).length > 0) {
        keys = Object.keys(sizeMap);
    } else {
        keys =
            Array.isArray(item.availableSizes) && item.availableSizes.length > 0
                ? [...item.availableSizes]
                : ["S", "M", "L", "XL"];
    }
    const uniq = [...new Set(keys.map(String))];
    uniq.sort((a, b) => {
        const ua = a.toUpperCase();
        const ub = b.toUpperCase();
        const ia = SIZE_ORDER.indexOf(ua);
        const ib = SIZE_ORDER.indexOf(ub);
        if (ia !== -1 || ib !== -1)
            return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        return a.localeCompare(b);
    });
    return uniq.map((key) => ({
        key,
        available: sizeMap ? Number(sizeMap[key]) > 0 : true,
    }));
}

function pickDefaultSize(options, preferred) {
    if (!options.length) return null;
    const pref = preferred != null ? String(preferred) : null;
    const match = options.find((o) => o.key === pref && o.available);
    if (match) return match.key;
    const firstAv = options.find((o) => o.available);
    return firstAv ? firstAv.key : options[0].key;
}


export function Wishlist() {
    const navigate = useNavigate();
    const { fetchProfile } = useProfile();
    const { showToast } = useToast();
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSizes, setSelectedSizes] = useState({});
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [sizeDropdowns, setSizeDropdowns] = useState({});

    /** Optional min/max for GET `/products/wish-list` — omitted when null so mob API stays compatible. */
    const appliedPriceFilterRef = useRef({ min: null, max: null });
    const [priceMinInput, setPriceMinInput] = useState("");
    const [priceMaxInput, setPriceMaxInput] = useState("");
    /** { item, intent: 'cart' | 'buynow' } | null */
    const [sizePicker, setSizePicker] = useState(null);
    const [pendingSize, setPendingSize] = useState(null);
    /** Wishlist row id pending remove confirmation */
    const [confirmRemoveItemId, setConfirmRemoveItemId] = useState(null);

    const applyPriceFilter = () => {
        const rawMin = priceMinInput.trim() === "" ? null : Number(priceMinInput);
        const rawMax = priceMaxInput.trim() === "" ? null : Number(priceMaxInput);
        if (rawMin != null && Number.isFinite(rawMin) && rawMax != null && Number.isFinite(rawMax) && rawMin > rawMax) {
            showToast({ message: "Minimum price cannot be greater than maximum price." });
            return;
        }
        appliedPriceFilterRef.current = {
            min: rawMin != null && Number.isFinite(rawMin) ? rawMin : null,
            max: rawMax != null && Number.isFinite(rawMax) ? rawMax : null,
        };
        fetchWishlistItems();
    };

    const clearAllFilters = () => {
        setPriceMinInput("");
        setPriceMaxInput("");
        appliedPriceFilterRef.current = { min: null, max: null };
        fetchWishlistItems();
    };

    useEffect(() => {
        if (!sizePicker) {
            setPendingSize(null);
            return;
        }
        const opts = getWishlistSizeOptions(sizePicker.item);
        const itemKey = sizePicker.item._id || sizePicker.item.id;
        const def = pickDefaultSize(opts, selectedSizes[itemKey] ?? sizePicker.item.selectedSize);
        setPendingSize(def);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- defaults when sheet opens
    }, [sizePicker]);

    useEffect(() => {
        if (!sizePicker) return;
        const onKey = (e) => {
            if (e.key === "Escape") setSizePicker(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [sizePicker]);

    useEffect(() => {
        if (confirmRemoveItemId == null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setConfirmRemoveItemId(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [confirmRemoveItemId]);

    const handleSizeSelect = (itemId, size) => {
        setSelectedSizes({ ...selectedSizes, [itemId]: size });
        setSizeDropdowns({ ...sizeDropdowns, [itemId]: false });
    };

    const handleQuantityChange = (itemId, change) => {
        const currentQty = selectedQuantities[itemId] || wishlistItems.find(item => item.id === itemId || item._id === itemId)?.selectedQty || 1;
        const newQty = Math.max(1, Math.min(10, currentQty + change));
        setSelectedQuantities({ ...selectedQuantities, [itemId]: newQty });
    };

    // Fetch wishlist items from API
    const fetchWishlistItems = async () => {
        try {
            setLoading(true);
            setError(null);

            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const transformedData = guestWishlist.map((item, index) => {
                    const price = item.price || 0;
                    const mrp = item.mrp || price;
                    const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
                    let imageUrl = '';
                    const rawUrl = (item.urls && Array.isArray(item.urls) && item.urls.length > 0) ? (item.urls[0] || item.urls[1]) : null;
                    if (rawUrl) {
                        imageUrl = ensureMediaUrl(String(rawUrl)) || '';
                    }
                    const productSizes =
                        item.size && typeof item.size === 'object' && !Array.isArray(item.size) ? item.size : {};
                    const availableSizesForProduct = Object.keys(productSizes).filter(
                        (size) => Number(productSizes[size]) > 0
                    );
                    const selectedSize =
                        item.selectedSize && Number(productSizes[item.selectedSize]) > 0
                            ? item.selectedSize
                            : availableSizesForProduct[0] || item.selectedSize || 'M';
                    const inStock = !isFullyOutOfStockFromSizeMap(productSizes);
                    const stockCount = inStock
                        ? Object.keys(productSizes).length > 0
                            ? totalStockFromSizeMap(productSizes)
                            : 10
                        : 0;
                    return {
                        id: item._id,
                        _id: item._id,
                        product_id: item.product_id,
                        image: imageUrl,
                        brand: item.brand || 'Unknown Brand',
                        title: item.title || 'Product',
                        seller: 'Seller',
                        price,
                        mrp,
                        discount,
                        selectedSize,
                        selectedQty: 1,
                        availableSizes:
                            availableSizesForProduct.length > 0
                                ? availableSizesForProduct
                                : ['S', 'M', 'L', 'XL'],
                        size: productSizes,
                        urls: item.urls || [],
                        rating: 4.0,
                        reviews: 0,
                        inStock,
                        stockCount,
                        badge: null,
                        deliveryDays: 3,
                        returnDays: 14
                    };
                });
                const { min, max } = appliedPriceFilterRef.current;
                let rows = transformedData;
                if (min != null && Number.isFinite(min)) rows = rows.filter((r) => r.price >= min);
                if (max != null && Number.isFinite(max)) rows = rows.filter((r) => r.price <= max);
                setWishlistItems(rows);
                setLoading(false);
                return;
            }
            
            const token = localStorage.getItem("token");
            if (!token) {
                console.warn("No token found, user may not be authenticated");
                setWishlistItems([]);
                setLoading(false);
                return;
            }

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const { min, max } = appliedPriceFilterRef.current;
            const params = {};
            if (min != null && Number.isFinite(min)) params.minPrice = min;
            if (max != null && Number.isFinite(max)) params.maxPrice = max;

            const response = await axios.get(
                `/products/wish-list`,
                { ...config, params }
            );

            const data = response.data;
            console.log('Wishlist API response:', data);

            // Backend returns { items, total, page, size } (paginated); legacy was a plain array.
            const rows = Array.isArray(data) ? data : (data?.items ?? []);

            // Transform API response to match component structure
            const transformedData = rows.map((item, index) => {
                // Get product data (similar to cart structure)
                const product = item.product_data || item.product || item;
                
                // Extract available sizes from product size object
                const productSizes = product.size || {};
                const availableSizesForProduct = Object.keys(productSizes).filter(
                    (size) => Number(productSizes[size]) > 0
                );

                const selectedSize =
                    item.selectedSize && Number(productSizes[item.selectedSize]) > 0
                        ? item.selectedSize
                        : availableSizesForProduct[0] || item.selectedSize || 'M';

                const inStock = !isFullyOutOfStockFromSizeMap(productSizes);
                const stockCount = inStock
                    ? Object.keys(productSizes).length > 0
                        ? totalStockFromSizeMap(productSizes)
                        : 10
                    : 0;

                // Calculate discount
                const price = product.price || 0;
                const mrp = product.mrp || price;
                const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

                // Get image URL (handle both array and string)
                let imageUrl = '';
                if (product.urls) {
                    if (Array.isArray(product.urls) && product.urls.length > 0) {
                        imageUrl = ensureMediaUrl(product.urls[0]) || '';
                    } else if (typeof product.urls === 'string') {
                        imageUrl = ensureMediaUrl(product.urls) || '';
                    }
                } else if (product.image) {
                    imageUrl = ensureMediaUrl(product.image) || '';
                }

                return {
                    id: item._id || item.id || index + 1,
                    _id: item._id || item.id,
                    product_id: item.product_id || product._id,
                    image: imageUrl,
                    brand: product.brand?.[0] || product.brand || 'Unknown Brand',
                    title: product.title || 'Product',
                    seller: product.seller || 'Seller',
                    price: price,
                    mrp: mrp,
                    discount: discount,
                    selectedSize: selectedSize,
                    selectedQty: item.selectedQty || 1,
                    availableSizes: availableSizesForProduct.length > 0 ? availableSizesForProduct : ['S', 'M', 'L', 'XL'],
                    size: productSizes,
                    urls: Array.isArray(product.urls) ? product.urls : (product.urls ? [product.urls] : []),
                    rating: product.rating || 4.0,
                    reviews: product.reviews || product.reviewsCount || 0,
                    inStock: inStock,
                    stockCount: stockCount,
                    badge: product.badge || null,
                    deliveryDays: product.deliveryDays || 3,
                    returnDays: product.returnDays || 14
                };
            });

            setWishlistItems(transformedData);
        } catch (err) {
            console.error('Error fetching wishlist:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch wishlist items');
            // Fallback to reference data if API fails (for development)
            // In production, you might want to show an error message instead
            if (err.response?.status === 404) {
                console.warn('Wishlist API endpoint not found, using empty array');
                setWishlistItems([]);
            } else {
                setWishlistItems([]);
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch wishlist on component mount
    useEffect(() => {
        fetchWishlistItems();
    }, []);

    const toggleSizeDropdown = (itemId, e) => {
        if (e) {
            e.stopPropagation();
        }
        setSizeDropdowns({ ...sizeDropdowns, [itemId]: !sizeDropdowns[itemId] });
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideDropdown = event.target.closest('.cart-select') || 
                                           event.target.closest('.dropdown-menu');
            
            if (!isClickInsideDropdown) {
                setSizeDropdowns({});
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const breadcrumbs = [
        { label: 'Home', href: '/' },
        { label: 'My Account', href: '/account' },
        { label: 'Wishlist', href: '/wishlist', isActive: true }
    ];

    const [expandedFilters, setExpandedFilters] = useState({
        price: true,
        availability: true,
        stock: true
    });

    const toggleFilter = (filterName) => {
        setExpandedFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    // Handle remove item from wishlist (returns true on success)
    const handleRemoveItem = async (itemId) => {
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
                const filtered = guestWishlist.filter(item => String(item._id) !== String(itemId));
                localStorage.setItem('guestWishlist', JSON.stringify(filtered));
                setWishlistItems(prev => prev.filter(item => String(item._id || item.id) !== String(itemId)));
                return true;
            }

            const token = localStorage.getItem("token");
            if (!token) {
                showToast({ message: "Please login to remove items from wishlist" });
                return false;
            }

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            await axios.delete(
                `/products/wish-list/${itemId}`,
                config
            );

            setWishlistItems(prevItems => prevItems.filter(item => (item._id || item.id) !== itemId));
            return true;
        } catch (err) {
            console.error('Error removing wishlist item:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to remove item from wishlist';
            showToast({ message: errorMessage });
            return false;
        }
    };

    const executeConfirmedRemove = async () => {
        if (confirmRemoveItemId == null) return;
        const ok = await handleRemoveItem(confirmRemoveItemId);
        if (ok) setConfirmRemoveItemId(null);
    };

    const openSizePickerForItem = (item, intent) => {
        if (!item.inStock) return;
        setSizePicker({ item, intent });
    };

    const confirmSizeSelection = async () => {
        if (!sizePicker || pendingSize == null) return;
        const opts = getWishlistSizeOptions(sizePicker.item);
        const sel = opts.find((o) => o.key === pendingSize);
        if (!sel?.available) return;
        const { item, intent } = sizePicker;
        const itemKey = item._id || item.id;
        const sizeToAdd = pendingSize;
        setSelectedSizes((prev) => ({ ...prev, [itemKey]: sizeToAdd }));
        setSizePicker(null);
        if (intent === "buynow") {
            await handleAddToCart(item, { redirectToOrderSummary: true, forcedSize: sizeToAdd });
        } else {
            await handleAddToCart(item, { forcedSize: sizeToAdd });
        }
    };

    // Handle add to cart (options: { redirectToOrderSummary } to go to order-summary after add, e.g. for Buy Now)
    const handleAddToCart = async (item, options = {}) => {
        const { redirectToOrderSummary = false, forcedSize = null } = options;
        try {
            const isGuest = localStorage.getItem('guestUser') === 'true';
            if (isGuest) {
                const selectedSize =
                    forcedSize ?? (selectedSizes[item._id || item.id] || item.selectedSize || 'M');
                const selectedQty = selectedQuantities[item._id || item.id] || item.selectedQty || 1;
                const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
                const productId = item.product_id || item._id;
                const existingIndex = guestCart.findIndex((c) => String(c.product_id) === String(productId));
                if (existingIndex >= 0) {
                    guestCart[existingIndex].selectedQty = (guestCart[existingIndex].selectedQty || 1) + selectedQty;
                } else {
                    guestCart.push({
                        _id: `guest-cart-${Date.now()}-${productId}`,
                        product_id: productId,
                        urls: item.urls || (item.image ? [item.image] : []),
                        title: item.title || 'Product',
                        price: item.price,
                        brand: item.brand,
                        mrp: item.mrp,
                        selectedSize,
                        selectedQty,
                        size: { [selectedSize]: 10 },
                        availableSizes: [selectedSize],
                        availableQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                        maxStock: 10
                    });
                }
                localStorage.setItem('guestCart', JSON.stringify(guestCart));
                window.dispatchEvent(new CustomEvent('guestCartUpdated'));
                if (redirectToOrderSummary) {
                    await saveCheckoutDraftForBuyNow(item, { selectedSize, selectedQty });
                    navigate('/order-summary');
                }
                return;
            }

            const token = localStorage.getItem("token");
            if (!token) {
                showToast({ message: "Please login to add items to cart" });
                return;
            }

            if (!item.inStock) {
                showToast({ message: "This item is out of stock" });
                return;
            }

            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            const selectedSize =
                forcedSize ??
                (selectedSizes[item._id || item.id] || item.selectedSize || item.availableSizes[0]);
            const selectedQty = selectedQuantities[item._id || item.id] || item.selectedQty || 1;

            const response = await axios.post(
                `/cart/${item.product_id || item._id}`,
                { selectedSize, selectedQty, pincode: '110001' },
                config
            );

            console.log('Item added to cart:', response.data);
            await fetchProfile(true);
            if (redirectToOrderSummary) {
                await saveCheckoutDraftForBuyNow(item, { selectedSize, selectedQty });
                navigate('/order-summary');
            }
        } catch (err) {
            console.error('Error adding to cart:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to add item to cart';
            showToast({ message: errorMessage });
        }
    };

    const sheetSizeOptions = sizePicker ? getWishlistSizeOptions(sizePicker.item) : [];
    const sheetHasStock = sheetSizeOptions.some((o) => o.available);
    const pendingOption = sheetSizeOptions.find((o) => o.key === pendingSize);

    return (
        <div className="wishlist-page-wrapper">
            {/* Breadcrumbs */}
            <div className="breadcrumbs-wrapper">
                <div className="breadcrumbs-container breadcrumbs-design-6">
                    {breadcrumbs.map((crumb, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && (
                                <span className="breadcrumbs-separator">/</span>
                            )}
                            {crumb.isActive ? (
                                <span className="breadcrumbs-current">{crumb.label}</span>
                            ) : (
                                <a href={crumb.href} className="breadcrumbs-link">{crumb.label}</a>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="wishlist-content-wrapper">
                {/* Filters Container */}
                <div className="wishlist-filters-container">
                    <div className="filters-header-wrapper">
                        <div className="filters-header">
                            <h3>Filters</h3>
                            <button type="button" className="clear-filters-btn" onClick={clearAllFilters}>
                                Clear All
                            </button>
                        </div>
                    </div>
                    
                    <div className="filters-content">
                        {/* Price Filter */}
                        <div className="filter-section">
                            <div className="filter-section-header" onClick={() => toggleFilter('price')}>
                                <span>PRICE</span>
                                <span className={`filter-toggle ${expandedFilters.price ? 'expanded' : ''}`}>
                                    {expandedFilters.price ? '×' : '+'}
                                </span>
                            </div>
                            {expandedFilters.price && (
                                <div className="filter-section-content">
                                    <div className="price-range-inputs">
                                        <input
                                            type="number"
                                            min={0}
                                            placeholder="Min"
                                            className="price-input wishlist-web-price-filter-input"
                                            value={priceMinInput}
                                            onChange={(e) => setPriceMinInput(e.target.value)}
                                            aria-label="Minimum price"
                                        />
                                        <span className="price-separator">-</span>
                                        <input
                                            type="number"
                                            min={0}
                                            placeholder="Max"
                                            className="price-input wishlist-web-price-filter-input"
                                            value={priceMaxInput}
                                            onChange={(e) => setPriceMaxInput(e.target.value)}
                                            aria-label="Maximum price"
                                        />
                                    </div>
                                    <button type="button" className="wishlist-web-apply-price-btn" onClick={applyPriceFilter}>
                                        Apply
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Availability Filter */}
                        <div className="filter-section">
                            <div className="filter-section-header" onClick={() => toggleFilter('availability')}>
                                <span>AVAILABILITY</span>
                                <span className={`filter-toggle ${expandedFilters.availability ? 'expanded' : ''}`}>
                                    {expandedFilters.availability ? '×' : '+'}
                                </span>
                            </div>
                            {expandedFilters.availability && (
                                <div className="filter-section-content">
                                    <div className="filter-checkbox-list">
                                        <label>
                                            <input type="checkbox" />
                                            <span>In Stock</span>
                                        </label>
                                        <label>
                                            <input type="checkbox" />
                                            <span>Out of Stock</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Stock Filter */}
                        <div className="filter-section">
                            <div className="filter-section-header" onClick={() => toggleFilter('stock')}>
                                <span>STOCK</span>
                                <span className={`filter-toggle ${expandedFilters.stock ? 'expanded' : ''}`}>
                                    {expandedFilters.stock ? '×' : '+'}
                                </span>
                            </div>
                            {expandedFilters.stock && (
                                <div className="filter-section-content">
                                    <div className="filter-checkbox-list">
                                        <label>
                                            <input type="checkbox" />
                                            <span>In Stock</span>
                                        </label>
                                        <label>
                                            <input type="checkbox" />
                                            <span>Low Stock</span>
                                        </label>
                                        <label>
                                            <input type="checkbox" />
                                            <span>Out of Stock</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Wishlist Content */}
                <div className="wishlist-desktop-base-cartLayout">
                    {loading ? (
                        <PageLoadLoader />
                    ) : error ? (
                        <div className="empty-wishlist">
                            <div className="empty-wishlist-icon">⚠️</div>
                            <h2>Error loading wishlist</h2>
                            <p>{error}</p>
                            <button type="button" className="empty-wishlist-retry-btn" onClick={fetchWishlistItems}>
                                Retry
                            </button>
                        </div>
                    ) : wishlistItems.length === 0 ? (
                        <div className="empty-wishlist">
                            <div className="empty-wishlist-icon">💔</div>
                            <h2>Your wishlist is empty</h2>
                            <p>Start adding items you love to your wishlist</p>
                        </div>
                    ) : (
                    <div className="itemBlock-base-container">
                        <div className="cartItemsList">
                            {wishlistItems.map((item, index) => (
                                <div key={item._id || item.id || index} className="itemContainer-base-itemMargin">
                                    <div className="itemContainer-base-item">
                                        {/* Badge on top right */}
                                        {item.discount > 0 ? (
                                            <div className="product-badge discount-badge-top">
                                                {item.discount}% OFF
                                            </div>
                                        ) : item.badge ? (
                                            <div className="product-badge">{item.badge}</div>
                                        ) : (
                                            <div className="product-badge new-badge">NEW</div>
                                        )}
                                        
                                        <div className="itemContainer-base-itemLeft">
                                            <img src={item.image} alt={item.title} />
                                            {!item.inStock && (
                                                <div className="out-of-stock-overlay">
                                                    <span>Out of Stock</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="itemContainer-base-itemRight">
                                            <div className="brand-and-seller">
                                                <div>{item.brand}</div>
                                                <a>{item.title}</a>
                                            </div>

                                            <div className="itemComponents-base-sellerContainer">
                                                <div className="itemComponents-base-sellerData">
                                                    Sold by: {item.seller}
                                                </div>
                                            </div>

                                            <div className="product-rating">
                                                <div className="rating-stars">
                                                    <BsStarFill className="star-icon" />
                                                    <span className="rating-value">{item.rating}</span>
                                                </div>
                                                <span className="rating-count">({item.reviews} reviews)</span>
                                            </div>

                                            <div className="itemContainer-base-price">
                                                <div>
                                                    <img src={rupeesIcon} alt="₹" />
                                                    {item.price}
                                                </div>
                                                <div>
                                                    <span>₹{item.mrp}</span>
                                                    <span className="discount-badge">{item.discount}% OFF</span>
                                                </div>
                                            </div>

                                            <div className="wishlist-inline-actions">
                                                <button 
                                                    className={`wishlist-action-btn-v6 buy-now-btn ${!item.inStock ? 'disabled' : ''}`}
                                                    disabled={!item.inStock}
                                                    type="button"
                                                    onClick={() => openSizePickerForItem(item, "buynow")}
                                                >
                                                    Buy Now
                                                </button>
                                                <button 
                                                    className={`wishlist-action-btn-v6 add-to-cart-btn ${!item.inStock ? 'disabled' : ''}`}
                                                    disabled={!item.inStock}
                                                    type="button"
                                                    onClick={() => openSizePickerForItem(item, "cart")}
                                                >
                                                    Add to Cart
                                                </button>
                                                <button
                                                    type="button"
                                                    className="wishlist-action-btn-v6 delete-btn"
                                                    onClick={() => setConfirmRemoveItemId(item._id || item.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>

                                        <div className="wishlist-additional-info additional-info-design-6">
                                            <div className="stock-status">
                                                {item.inStock ? (
                                                    <div className="in-stock">
                                                        <BsCheckCircle className="status-icon" />
                                                        In Stock ({item.stockCount} available)
                                                    </div>
                                                ) : (
                                                    <div className="out-of-stock">
                                                        Out of Stock
                                                    </div>
                                                )}
                                            </div>

                                            <div className="returnPeriod-base-returnItem return-delivery-row">
                                                <div className="returnPeriod-base-returnItem">
                                                    <div className="returnPeriod-base-returnIcon">
                                                        <img src={returnIcon} alt="Return" />
                                                    </div>
                                                    <div className="returnPeriod-base-returnText">
                                                        <span>{item.returnDays} days </span>
                                                        return available
                                                    </div>
                                                </div>

                                                <div className="returnPeriod-base-returnItem">
                                                    <div className="returnPeriod-base-returnIcon">
                                                        <img src={deliveryIcon} alt="Delivery" />
                                                    </div>
                                                    <div className="returnPeriod-base-returnText">
                                                        Delivery in
                                                        <span> {item.deliveryDays} days </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="wishlist-actions"  style={{display: "none"}}>
                                            <button 
                                                className={`wishlist-action-btn-v6 add-to-cart-btn ${!item.inStock ? 'disabled' : ''}`}
                                                disabled={!item.inStock}
                                            >
                                                Add to Cart
                                            </button>
                                            <button className="wishlist-action-btn-v6 quick-view-btn">
                                                Quick View
                                            </button>
                                            <button className="wishlist-action-btn-v6 delete-btn">
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
            </div>

            {confirmRemoveItemId != null && (
                <div
                    className="wishlist-web-remove-confirmation-modal"
                    role="presentation"
                    onClick={() => setConfirmRemoveItemId(null)}
                >
                    <div
                        className="wishlist-web-remove-confirmation-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="wishlist-remove-confirm-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="wishlist-remove-confirm-title">Remove from wishlist?</h3>
                        <p>This item will be removed from your wishlist.</p>
                        <div className="wishlist-web-remove-confirmation-buttons">
                            <button type="button" className="cancel-btn" onClick={() => setConfirmRemoveItemId(null)}>
                                Cancel
                            </button>
                            <button type="button" className="remove-btn" onClick={executeConfirmedRemove}>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {sizePicker && (
                <div
                    className="wishlist-size-sheet-overlay wishlist-size-sheet-overlay--web"
                    role="presentation"
                    onClick={() => setSizePicker(null)}
                >
                    <div
                        className="wishlist-size-sheet wishlist-size-sheet--web"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="wishlist-web-size-sheet-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="wishlist-size-sheet-handle" aria-hidden />
                        <h3 id="wishlist-web-size-sheet-title" className="wishlist-size-sheet-title">
                            Select size
                        </h3>
                        <p className="wishlist-size-sheet-product">{sizePicker.item.title}</p>
                        <div className="wishlist-size-chip-grid" role="group" aria-label="Sizes">
                            {sheetSizeOptions.map(({ key, available }) => (
                                <button
                                    key={key}
                                    type="button"
                                    disabled={!available}
                                    title={!available ? "Out of stock" : undefined}
                                    className={`wishlist-size-chip ${pendingSize === key ? "is-selected" : ""} ${!available ? "is-disabled" : ""}`}
                                    onClick={() => available && setPendingSize(key)}
                                    aria-pressed={pendingSize === key}
                                    aria-disabled={!available}
                                    aria-label={!available ? `${key}, out of stock` : key}
                                >
                                    {key}
                                </button>
                            ))}
                        </div>
                        {!sheetHasStock ? (
                            <p className="wishlist-size-sheet-oos">This product is out of stock in all sizes.</p>
                        ) : null}
                        <div className="wishlist-size-sheet-actions">
                            <button type="button" className="wishlist-size-sheet-btn wishlist-size-sheet-btn-secondary" onClick={() => setSizePicker(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="wishlist-size-sheet-btn wishlist-size-sheet-btn-primary"
                                disabled={pendingSize == null || !pendingOption?.available || !sheetHasStock}
                                onClick={confirmSizeSelection}
                            >
                                {sizePicker.intent === "buynow" ? "Buy now" : "Add to cart"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
